/**
 * Wheel-input classification for board navigation — the pure decision layer
 * that turns a raw `wheel` event into a zoom-or-pan intent, device-aware.
 *
 * Split out of `useWheelZoom` so the (previously untestable, inline) heuristic
 * can be exercised directly. The old inline test — `deltaMode !== 0` meaning
 * "physical mouse wheel" — only holds on FIREFOX. Chrome / Edge / Safari /
 * Electron / JCEF-CEF / VSCode-webview (every real archlang board surface) fire
 * a physical mouse detent as `deltaMode = 0` (pixel), `deltaY` a large integer
 * (a multiple of ~120, sometimes 100/150). So on the actual product a mouse
 * wheel was mis-detected as a trackpad scroll and PANNED instead of zooming,
 * and a mouse ctrl-wheel hit the trackpad-tuned pinch gain (deltaY≈120 × 0.03 =
 * a 36× jump per detent). This module fixes both.
 *
 * Model mirrors the industry leaders (Miro/Figma): wheel semantics are
 * DEVICE-dependent. A mouse wheel zooms (zoom-to-cursor); a trackpad two-finger
 * scroll pans and ctrl/pinch zooms. Device is auto-detected per event — there
 * is no user-facing mode selector, exactly like Miro/Figma.
 */

/** The gains the classifier needs — injected so the single source of truth
 *  stays in `viewportHooks.ts` rather than duplicated here. */
export interface WheelGains {
  /** Trackpad pinch (ctrl/meta-wheel): log-zoom per unit of raw deltaY. */
  readonly pinch: number;
}

/** The subset of a `WheelEvent` the classifier reads — a plain object so it is
 *  trivially constructible in tests and from the IntelliJ native bridge. */
export interface WheelSample {
  readonly deltaX: number;
  readonly deltaY: number;
  /** 0 = pixel, 1 = line, 2 = page (per the DOM `WheelEvent.deltaMode`). */
  readonly deltaMode: number;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
}

/** The resolved intent. `zoom` carries a log-space delta ready for `zoomAt`
 *  (positive = zoom in). `pan` carries screen-space deltas already negated for
 *  the accumulator (i.e. add them straight onto pending pan). */
export type WheelIntent =
  | { readonly kind: "zoom"; readonly zoomDelta: number }
  | { readonly kind: "pan"; readonly panDx: number; readonly panDy: number };

/** Fixed per-detent zoom step for a mouse wheel: one notch = ~15%, regardless
 *  of the OS-reported delta magnitude (which is quantised and varies 100/120/
 *  150 across platforms). Predictable and un-twitchy — the opposite of scaling
 *  by the raw deltaY. */
export const MOUSE_ZOOM_STEP = Math.log(1.15);

/**
 * Heuristic: is this wheel event a physical mouse detent (vs a trackpad)?
 *
 * - Line/page mode (`deltaMode !== 0`) → a discrete wheel (Firefox mice).
 * - Pixel mode: a physical detent is a large, purely-vertical, integer delta.
 *   A trackpad reports a horizontal component, small magnitudes, or fractional
 *   pixels — and a trackpad PINCH (ctrl-wheel) sends tiny deltaY (1–4), which
 *   also fails these tests and is correctly treated as a trackpad.
 */
export function inferMouseWheel(e: WheelSample): boolean {
  if (e.deltaMode !== 0) return true;
  if (e.deltaX !== 0) return false;
  const ay = Math.abs(e.deltaY);
  if (ay < 50) return false;
  return Number.isInteger(e.deltaY);
}

/**
 * Classify a wheel event into a zoom-or-pan intent, device auto-detected.
 *
 * MOUSE device: the wheel zooms (zoom-to-cursor via the pivot the caller
 * passes to `zoomAt`); shift-wheel pans horizontally; ctrl/meta still zooms
 * (browser convention — never the runaway pinch gain).
 *
 * TRACKPAD device: two-finger scroll pans 1:1; ctrl/meta (pinch) zooms with
 * the pinch gain.
 */
export function classifyWheel(e: WheelSample, gains: WheelGains): WheelIntent {
  const device: "mouse" | "trackpad" = inferMouseWheel(e) ? "mouse" : "trackpad";
  const zoomMod = e.ctrlKey || e.metaKey;

  if (device === "mouse") {
    // Shift-wheel → horizontal pan. Browsers often already move the delta onto
    // deltaX for shift-scroll; fall back to deltaY when they don't.
    if (e.shiftKey && !zoomMod) {
      const d = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      return { kind: "pan", panDx: -d, panDy: 0 };
    }
    // Fixed step per detent, direction from the sign (wheel up, deltaY < 0 →
    // zoom in → positive log-delta).
    const dir = -Math.sign(e.deltaY);
    return { kind: "zoom", zoomDelta: dir * MOUSE_ZOOM_STEP };
  }

  // Trackpad.
  if (zoomMod) {
    return { kind: "zoom", zoomDelta: -e.deltaY * gains.pinch };
  }
  return { kind: "pan", panDx: -e.deltaX, panDy: -e.deltaY };
}
